import { useEffect, useState } from 'react';
import Modal from './Modal';
import DatePicker from '../../Calendar/DatePicker';
import SearchFormWrapper from './SearchFormWrapper';
import { getImage } from '../../utils/pathHelper';
import CalendarIcon from '../images/calendar.svg';
import InfoSvg from '../images/info.svg';
import styles from '../styles/HotelPlansSearch.module.scss';
import { differenceInDays } from 'date-fns';
import i18n from '../../i18n';
import { LocalizedTotalPrice } from '../types';
import FormCheckbox from '../../Form/FormCheckbox';
import { formatDateWithDayOfWeek } from '../../utils/dateHelper';

type SearchFormDateProps = {
  selectedDateList: Date[];
  minDate: Date;
  maxDate?: Date;
  priceCalendar?: { date: string, price: LocalizedTotalPrice }[];
  handleDateListChange: (selectedDateList: Date[]) => void;
  minimumStayDays?: number;
  flexGrow?: number;
  flexShrink?: number;
}

const SearchFormDate: React.FC<SearchFormDateProps> = ({ selectedDateList, minDate, maxDate, priceCalendar, handleDateListChange, minimumStayDays, flexGrow, flexShrink }) => {
  const [isModalShown, setModalShown] = useState(false);
  const [isFlexibleDate, setIsFlexibleDate] = useState<boolean>(false);
  const checkinDate = selectedDateList[0];
  const checkoutDate = selectedDateList.length > 1 ? selectedDateList.slice(-1)[0] : null;
  const isDateSelected = selectedDateList.length > 0;
  const information = priceCalendar?.reduce((result, item) => {
    const price = item.price;
    const priceWithCurrency = i18n.locale === 'ja' ? `${price.amount}${price.currency}` : `${price.currency} ${price.amount}`;
    result[item.date] = priceWithCurrency;
    return result;
  }, {});

  useEffect(() => {
    setIsFlexibleDate(selectedDateList.length === 0)
  }, [selectedDateList]);

  const onChangeIsFlexibleDate = (e) => {
    if (e.target.checked) {
      handleDateListChange([]);
    };
    setIsFlexibleDate(e.target.checked);
  };

  return (
    <SearchFormWrapper
      onClick={() => setModalShown(true)}
      icon={<img src={getImage(CalendarIcon)} />}
      flexGrow={flexGrow}
      flexShrink={flexShrink}
    >
      {isDateSelected ? `${formatDateWithDayOfWeek(checkinDate)} - ${checkoutDate ? formatDateWithDayOfWeek(checkoutDate) : ''}` : i18n.t('stay_packages.shared.flexible_dates')}
      <div className={styles.dateForm}>
        <Modal isShown={isModalShown} handleClose={() => { setModalShown(false); }} title={''} whiteMode={true}>
          <DatePicker selectedDateList={selectedDateList}
            currentMonth={selectedDateList[0]}
            minDate={minDate}
            maxDate={maxDate}
            information={information}
            styles={styles}
            handleDateListChange={(selectedDateList: Date[]) => handleDateListChange(selectedDateList)}
          />
          {minimumStayDays > 1 && (
            <div className={styles.reservationDateInfo}>
              <img src={getImage(InfoSvg)} alt='info' />
              <div>
                {i18n.t('stay_packages.hotels.index.reservations_accepted_from', {days: minimumStayDays})}
              </div>
            </div>
          )}
          <div className='d-flex justify-content-center my-3'>
            <FormCheckbox
              id='flexible-date-checkbox'
              label={i18n.t('stay_packages.shared.flexible_dates')}
              checked={isFlexibleDate}
              theme='primary'
              onChange={onChangeIsFlexibleDate}
            />
          </div>
          <hr />
          <div className={styles.dateFormValues}>
            {isFlexibleDate ? (
              <>{i18n.t('stay_packages.shared.flexible_dates')}</>
            ) : (
              <>
                <div>
                  {checkinDate ? formatDateWithDayOfWeek(checkinDate) : i18n.t('accommodations.checkin')}
                  ~
                  {checkoutDate ? formatDateWithDayOfWeek(checkoutDate) : i18n.t('accommodations.checkout')}
                </div>
                {selectedDateList.length > 1 && (
                  <div>
                    {differenceInDays(checkoutDate, checkinDate) === 1
                      ? i18n.t('base.number_of_stays.one')
                      : i18n.t('base.number_of_stays.other', { count: differenceInDays(checkoutDate, checkinDate) })
                    }
                  </div>
                )}
              </>
            )}
          </div>

          <div className="d-flex justify-content-center mt-4">
            <button
              className="col-12 col-md-5 btn btn-primary rounded-pill font-weight-bold"
              onClick={() => setModalShown(false)}>
              { i18n.t('base.confirm') }
            </button>
          </div>
        </Modal>
      </div>
    </SearchFormWrapper>
  );
}

export default SearchFormDate;
